import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { FieldInput, Popover } from "@/components/ds";

/**
 * GlobalSearchBar — Phase 5 of the billing-feature-parity build (see
 * docs/architecture — "admin global search bar"). Lets any Velaro staff
 * member jump straight to a site/customer by name, site ID, or an admin's
 * email, from anywhere in the app.
 *
 * Reuses the EXISTING SupportTools/sites/search endpoint (SupportToolsController.
 * SearchSites) rather than adding a new one — it already does name/ID lookup,
 * staff-only auth (IsVelaroAdminRoleAsync), and v10/v20 migration filtering. The
 * only backend change for this phase was widening its fuzzy-match branch to also
 * match an active admin's email, so this bar's three documented lookup keys (name,
 * ID, email) are all real.
 *
 * Renders nothing for a caller who isn't a Velaro Admin — a 403 from the search
 * endpoint just clears the dropdown with a quiet inline message rather than
 * throwing, since most admin users will never see this box trigger a request at
 * all (they'd have to type into it first).
 */

interface GlobalSearchSiteResult {
  siteId: number;
  companyName: string;
  companyURL: string | null;
  isTestSite: boolean | null;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export default function GlobalSearchBar() {
  const api = useApi();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchSiteResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputWrapRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setError("");
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError("");
      try {
        const res = await api.admin.get(
          `SupportTools/sites/search?q=${encodeURIComponent(trimmed)}`
        );
        // RestService resolves (never rejects) on a non-ok status, including 403 —
        // it must be checked explicitly here rather than relying on a catch block,
        // which never fires for it.
        if (!res.ok) {
          if (requestId !== requestIdRef.current) return;
          setResults([]);
          setError(
            res.status === 403
              ? "You don't have access to search sites."
              : "Search failed."
          );
          setOpen(true);
          return;
        }
        const data: unknown = await res.json();
        if (requestId !== requestIdRef.current) return;
        // A slower earlier request resolving after a newer one must not clobber
        // fresher results — only the most recent debounced request may apply.
        setResults(Array.isArray(data) ? (data as GlobalSearchSiteResult[]) : []);
        setOpen(true);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setError("Search failed.");
        setOpen(true);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, api]);

  // Click outside closes the dropdown (input keeps its typed value).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (inputWrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  function goToSite(site: GlobalSearchSiteResult) {
    setOpen(false);
    setQuery("");
    setResults([]);
    navigate(`/admin/support-tools?tab=site-tools&siteId=${site.siteId}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter" && results.length === 1) {
      goToSite(results[0]);
    }
  }

  return (
    <div
      ref={inputWrapRef}
      className="relative w-full max-w-xs"
      role="search"
      aria-label="Search sites"
    >
      <FieldInput
        type="search"
        value={query}
        placeholder="Search site, company, or email…"
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (results.length > 0 || error) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        aria-label="Search sites by name, ID, or email"
      />

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={inputWrapRef}
        placement="bottom-start"
        minWidth={280}
      >
        {loading && <Popover.Label>Searching…</Popover.Label>}
        {!loading && error && (
          <div className="px-3 py-2 text-sm text-red-600 dark:text-red-400">{error}</div>
        )}
        {!loading && !error && results.length === 0 && (
          <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
            No matching sites.
          </div>
        )}
        {!loading &&
          !error &&
          results.slice(0, 8).map((site) => (
            <Popover.Item key={site.siteId} onClick={() => goToSite(site)}>
              <span className="flex flex-col items-start min-w-0">
                <span className="truncate w-full">
                  {site.companyName}
                  {site.isTestSite && (
                    <span className="ml-2 text-xs text-zinc-400 dark:text-zinc-400">Test</span>
                  )}
                </span>
                <span className="text-xs text-zinc-400 dark:text-zinc-400">
                  #{site.siteId}
                  {site.companyURL ? ` · ${site.companyURL}` : ""}
                </span>
              </span>
            </Popover.Item>
          ))}
      </Popover>
    </div>
  );
}
